/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React from 'react';
import { formatCurrency } from '../../utils/formatters.ts';
import {
  calculateGrossTotal,
  calculateNetPayment,
  calculateNetTotal,
  calculateMerchSherpaFee,
  calculateNetProfit,
  getOrderPrintfulCosts,
  getOrderPrintfulDiscount,
  getOrderYoycolCosts,
  getOrderFulfillmentCost,
  getShopifyShippingRevenue
} from '../../utils/calculations.ts';

interface OrderTotalsExpandedProps {
  order: any;
}

const OrderTotalsExpanded: React.FC<OrderTotalsExpandedProps> = ({ order }) => {
  const printfulTotals = getOrderPrintfulCosts(order);
  const yoycolTotals = getOrderYoycolCosts(order);
  const grossTotal = calculateGrossTotal(order.lineItems);
  const shopifyPaymentFee = parseFloat(order.shopifyPaymentFee?.toString()) || 0;
  const tip = parseFloat(order.shopifyTip?.toString()) || 0;
  const printfulDiscount = getOrderPrintfulDiscount(order);

  // Combined across every fulfillment vendor on this order — same formula
  // ShopifySalesGrid uses for "Total Earned", so the two never drift apart.
  const calculatedFulfillmentCost = getOrderFulfillmentCost(order);
  const shopifyShippingRevenue = getShopifyShippingRevenue(order);
  const productBaseCost = printfulTotals.subtotal + yoycolTotals.subtotal;
  const fulfillmentShipping = printfulTotals.shipping + yoycolTotals.shipping;
  const salesTax = printfulTotals.tax + yoycolTotals.tax;
  
  const grossTotalWithTip = grossTotal + tip + shopifyShippingRevenue;
  const netPayment = calculateNetPayment(grossTotalWithTip, shopifyPaymentFee);
  const netTotal = calculateNetTotal(netPayment, calculatedFulfillmentCost);
  
  // Include tip when calculating fees (matching sales grid logic)
  const adjustedNetTotal = netTotal + printfulDiscount;
  const merchSherpaFee = calculateMerchSherpaFee(adjustedNetTotal);
  const netProfit = calculateNetProfit(adjustedNetTotal, merchSherpaFee);
  
  // Total earned is just the net profit (tip already included in calculations)
  const totalEarned = netProfit;
  
  return (
    <div className="expanded-section order-totals-expanded">
      <h4 className="expanded-title">
        Order Totals for #{order.orderNumber}
      </h4>
      <div className="order-totals-section">
        <div className="financial-summary-section">
          <div className="summary-grid">
            <div className="grid-block panel--wide">
              <h5>Cash In</h5>
              <div className="total-item">
                <span className="bold">Order Cost:</span>
                <span className="num-value">{formatCurrency(grossTotal)}</span>
              </div>
              {tip > 0 && (
                <div className="total-item positive">
                  <span className="bold">Customer Donation:</span>
                  <span className="num-value positive">+{formatCurrency(tip)}</span>
                </div>
              )}
              {shopifyShippingRevenue > 0 && (
                <div className="total-item positive">
                  <span className="bold">Shopify Shipping Charged:<br/>
                    <span className="sub-text">(Yoycol's actual shipping cost is shown below, not this charge)</span>
                  </span>
                  <span className="num-value positive">+{formatCurrency(shopifyShippingRevenue)}</span>
                </div>
              )}
              <div className="total-item">
                <span className="bold">Gross Total:</span>
                <span className="num-value">{formatCurrency(grossTotalWithTip)}</span>
              </div>
              {shopifyPaymentFee > 0 && (
                <div className="total-item">
                  <span className="bold">Payment Platform Fee:<br/>
                    <span className="sub-text">(credit card, Apple pay, etc.)</span>
                  </span>
                  <span className="num-value negative">-{formatCurrency(shopifyPaymentFee)}</span>
                </div>
              )}
              <div className="total-item green">
                <span className="bold">Net Payment:<br/>
                   <span className="sub-text">
                    (order cost {tip > 0 && '+ tip'} - payment platform fee)
                   </span>
                </span>
                <span className="num-value positive">{formatCurrency(netPayment)}</span>
              </div>
            </div>
            <div className="grid-block panel--wide">
              <h5>Fulfillment Costs</h5>
              <div className="total-item">
                <span className="bold">Product Base Cost:</span>
                <span className="num-value">-{formatCurrency(productBaseCost)}</span>
              </div>
              <div className="total-item">
                <span className="bold">Fulfillment Shipping:</span>
                <span className="num-value">-{formatCurrency(fulfillmentShipping)}</span>
              </div>
              <div className="total-item">
                <span className="bold">Digitization Fee (one time):</span>
                <span className="num-value negative">-{formatCurrency(printfulTotals.digitization)}</span>
              </div>
              <div className="total-item">
                <span className="bold">Retail Delivery Fee (state specific):</span>
                <span className="num-value">-{formatCurrency(printfulTotals.retailDeliveryFee)}</span>
              </div>
              <div className="total-item">
                <span className="bold">Sales Tax:</span>
                <span className="num-value">-{formatCurrency(salesTax)}</span>
              </div>
              <div className="total-item">
                <span className="bold">Total Fulfillment Cost:</span>
                <span className="num-value">-{formatCurrency(calculatedFulfillmentCost)}</span>
              </div>
            </div>
            <div className="grid-block panel--wide">
              <h5>Profit</h5>
              <div className="total-item green">
                <span className="bold">Net Total (payment - cost):</span>
                <span className="num-value positive">{formatCurrency(netTotal)}</span>
              </div>

              {printfulDiscount > 0 && (
                <div className="total-item sherpa-blue">
                  <span className="bold">
                    Sherpa Network Discount:
                  </span>
                  <span className="num-value positive" style={{color: '#0c5460', fontWeight: 'bold'}}>
                    +{formatCurrency(printfulDiscount)}
                  </span>
                </div>
              )}
              <div className="total-item">
                <span className="bold">Merch Sherpa Platform (20%):</span>
                <span className="num-value negative">-{formatCurrency(merchSherpaFee)}</span>
              </div>
              {/* <div className="total-item green">
                <span className="bold">Net Profit:</span>
                <span className="num-value positive">{formatCurrency(netProfit)}</span>
              </div> */}

              <div className="total-item green">
                <span className="bold">Total Earned:</span>
                <span className="num-value bolded positive">{formatCurrency(totalEarned)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderTotalsExpanded;
